import classNames from "classnames";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router";
import {
  DownloadIcon,
  SaveIcon,
  TrashIcon,
} from "../../../../../../assets/icons";
import { ModelsItemType } from "../../../../../models";
import {
  downloadFile,
  isViewedItem,
  saveViewedItem,
} from "../../../../../utils";
import { getText, sanitizeHtml } from "../../../../../utils/_generic";
import {
  getHighlightedExcerpts,
  highlightFullText,
} from "../../../../../utils/_getHighlightedExcerpts";
import { saveDocumentWithNotify } from "../../../../../utils/_handleSaveDocument";
import PDLTypography from "../../../dna/_pdltypography";
import { ActionButton } from "../../../global/_actionButton";

export interface ModelsItemProps {
  item: ModelsItemType;
  index: number;
  searchTerm?: string;
  pushDown?: boolean;
  cardProfileStyle?: boolean;
  onRemove?: (item: ModelsItemType) => void;
}

const ModelItem = ({
  item,
  index,
  searchTerm,
  pushDown = false,
  cardProfileStyle = false,
  onRemove,
}: ModelsItemProps) => {
  const dispatch = useDispatch();
  const [isSavingDocument, setIsSavingDocument] = useState(false);
  const itemId = item.id || `generic-${index}`;
  const sanitizedHtml = sanitizeHtml(item.texto || "");
  const textOnly = getText(sanitizedHtml);

  const excerpt = searchTerm
    ? getHighlightedExcerpts(textOnly, searchTerm, 150)
    : null;

  const opened = isViewedItem(itemId) && !cardProfileStyle;

  const handleDownloadFile = () => {
    const wordUrl = `https://produtos.lex.com.br${item.versaoWord}`;

    downloadFile({
      name: item.titulo || "arquivo.docx",
      url: wordUrl,
    });
  };

  const fullTitle = item?.titulo || "";

  const highlightedTitle = highlightFullText(fullTitle || "", searchTerm);

  const displayTitle = highlightedTitle ? highlightedTitle : fullTitle || "";

  const cleanTitle = sanitizeHtml(displayTitle);

  const handleSaveDocument = () =>
    saveDocumentWithNotify({
      dispatch,
      setIsSavingDocument,
      documentoId: item?._id,
      id: item?.id,
      tipo: "Modelos",
      url: window.location.href,
    });

  const renderActions = (className: string) => {
    return (
      <div className={classNames("btn-action-container flex gap-2", className)}>
        <ActionButton
          icon={<SaveIcon />}
          onClick={handleSaveDocument}
          disabled={isSavingDocument}
          label={isSavingDocument ? "Salvando..." : "Salvar"}
        />
        <ActionButton
          icon={<DownloadIcon />}
          label="Baixar em Word"
          onClick={handleDownloadFile}
        />
      </div>
    );
  };

  return (
    <div
      key={itemId}
      id={`result-item-${itemId}`}
      className={`pdl_components_lista_resultados_item mt-3 ${
        opened ? "opened" : ""
      } ${pushDown ? "push-down" : ""} ${cardProfileStyle ? "card-style" : ""}`}
    >
      {renderActions("mobile")}
      <header className="title-wrapper">
        <Link
          to={`/modelos/${item.id}`}
          className="text-decoration-none"
          onClick={() => {
            saveViewedItem(itemId);
            sessionStorage.setItem("last-clicked-result-item", itemId);
          }}
        >
          <PDLTypography
            size="md"
            weight="semibold"
            variant="brand"
            upper
            className="title-link"
          >
            <span
              className="pdl-title-clamp-3 item-title"
              dangerouslySetInnerHTML={{
                __html: `${
                  opened ? `<span class="opened-status">Acessado</span>` : ""
                } ${cleanTitle}`,
              }}
            />
          </PDLTypography>
        </Link>
      </header>

      <div className="result-meta mb-2 mt-2 flex justify-between items-start">
        <div className="flex">
          <PDLTypography as="span" size="sm">
            Modelos
          </PDLTypography>
          {item.tipo && (
            <>
              &nbsp;&bull;&nbsp;
              <PDLTypography as="span" size="sm">
                {item.tipo}
              </PDLTypography>
            </>
          )}
          {item.area?.length > 0 && (
            <>
              &nbsp;&bull;&nbsp;
              <PDLTypography as="span" size="sm">
                {item.area}
              </PDLTypography>
            </>
          )}
          {item.categoria && (
            <>
              &nbsp;&bull;&nbsp;
              <PDLTypography as="span" size="sm">
                {item.categoria}
              </PDLTypography>
            </>
          )}
        </div>
        {renderActions("desktop")}
      </div>

      <div className="text-container">
        {!!excerpt && (
          <PDLTypography
            size="sm"
            weight="medium"
            className="text-gray-800 mb-1"
          >
            Texto encontrado em:
          </PDLTypography>
        )}

        <div
          className="content text-sm text-gray-800"
          dangerouslySetInnerHTML={{
            __html: excerpt || textOnly.slice(0, 500),
          }}
        />
      </div>

      {pushDown && (
        <div className="btn-action-container-footer">
          <div className="btn-action-group-left">
            <ActionButton
              icon={<DownloadIcon />}
              label="Baixar em Word"
              onClick={handleDownloadFile}
            />
          </div>
          {onRemove && (
            <div className="btn-action-group-right">
              <button
                type="button"
                className="btn-remove-saved"
                onClick={() => onRemove(item)}
              >
                <TrashIcon color="#8c0000" />
                <span>Remover de salvos</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ModelItem;
